import { PaymentListQueryVariables } from "@/gql/graphql";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatDate } from "@/lib/Formatters/formatDate";
import {
  PaymentListItem,
  paymentListQuery,
} from "@/app/Payments/GraphQL/paymentListQuery";
import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";
import { Badge } from "@/components/catalyst/badge";
import { paymentStatusColorMap } from "@/app/Payments/Utils/paymentColorMaps";
import {
  DataTable,
  DataTableColDef,
} from "@/lib/Components/DataTable/DataTable";
import type { TableId } from "@/app/Common/Utils/table-id";
import { usePaymentActions } from "@/app/Payments/Hooks/usePaymentActions";
import { ReactNode } from "react";
import { TimeSinceNowCell } from "@/lib/Components/DataTable/TimeSinceNowCell";
import { Text } from "@/components/catalyst/text";
import { Link } from "@/components/catalyst/link";
import { DeEmphasisedCell } from "@/lib/Components/DataTable/DeEmphasisedCell";

type PaymentTableProps = {
  queryVariables?: Partial<PaymentListQueryVariables>;
  id: TableId;
  title?: string;
  rightButtons?: ReactNode;
  searchable?: boolean;
  hiddenColumns?: PaymentTableColumnId[];
  columnToggles?: boolean;
};

type PaymentTableColumnId =
  | "name"
  | "status"
  | "due_date"
  | "amount"
  | "payee"
  | "fulfilled_by"
  | "description"
  | "created_at";

export function PaymentTable({
  queryVariables,
  id,
  title,
  rightButtons,
  searchable,
  hiddenColumns,
  columnToggles,
}: PaymentTableProps) {
  "use no memo";
  const getActions = usePaymentActions();

  const columns: DataTableColDef<PaymentListItem, any, PaymentTableColumnId>[] =
    [
      {
        id: "name",
        header: "Payment",
        accessorFn: (row) => row,
        cell: ({ row }) => (
          <ModelLinkCell
            from="/$tenant/$orgId/supplier/{-$supplierId}"
            to={"/$tenant/$orgId/supplier/{-$supplierId}/payments/$id"}
            params={(prev) => ({
              ...prev,
              id: row.original.id,
            })}
            viewTransition
          >
            {row.original.id}
          </ModelLinkCell>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (row) => row,
        cell: ({ row }) => (
          <Badge color={paymentStatusColorMap[row.original.status]}>
            {row.original.status}
          </Badge>
        ),
      },
      {
        id: "due_date",
        header: "Due",
        accessorFn: (row) => formatDate(row.due_date),
        cell: DeEmphasisedCell,
      },
      {
        id: "amount",
        header: "Amount",
        accessorFn: (row) => formatCurrency(row.amount, row.currency),
      },
      {
        id: "payee",
        header: "Payee",
        accessorFn: (row) => row,
        cell: ({ row }) =>
          row.original.payee ? (
            <ModelLinkCell
              to="."
              search={(prev: Record<string, unknown>) => ({
                ...prev,
                contactId: row.original.payee?.id,
              })}
            >
              {row.original.payee.legal_name ?? row.original.payee.name}
            </ModelLinkCell>
          ) : (
            <div>--</div>
          ),
      },
      {
        id: "fulfilled_by",
        header: "Fulfills",
        accessorFn: (row) => row,
        cell: ({ row }) => {
          if (row.original.fulfilledBy?.__typename === "PayableLineItem") {
            const objectId = row.original.fulfilledBy.object_id;
            return (
              <Link
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/invoices/$id"
                params={(prev) => ({
                  ...prev,
                  id: objectId,
                })}
                className="text-blue-500 underline"
              >
                INV{objectId.padStart(6, "0")}
              </Link>
            );
          }

          if (row.original.fulfilledBy?.__typename === "StripePayment") {
            return <div>{row.original.fulfilledBy.payment_intent_id}</div>;
          }
        },
      },
      {
        id: "description",
        header: "Description",
        accessorFn: (row) => row.description,
        cell: ({ row }) => <Text>{row.original.description}</Text>,
      },
      {
        id: "created_at",
        header: "Created",
        accessorFn: (row) => row.created_at,
        cell: TimeSinceNowCell,
      },
    ];

  return (
    <DataTable
      title={title}
      hiddenColumns={hiddenColumns}
      rightButtons={rightButtons}
      searchable={searchable}
      columnToggles={columnToggles}
      document={paymentListQuery}
      getQueryVariables={({ pagination }) => {
        return {
          first: pagination.pageSize,
          page: pagination.pageIndex,
          ...queryVariables,
        };
      }}
      getActions={getActions}
      accessor={(data) => data.payments}
      columns={columns}
      id={id}
    />
  );
}
